import React, { useCallback, useState } from "react";

import { useGetVerificationEditingRequests, useTranslation } from "utils";

import { EditingRequestType, editingRequestIdentifierMap } from "models";

import { AlertBanner, Form, TextArea } from "components";

export type VerificationEditingRequestProps = {
  type: EditingRequestType;
  farmId: string;
  id: string;
  requestId?: string;
};

export const EditingRequestBanner = ({ type, farmId, id, requestId = undefined }: VerificationEditingRequestProps) => {
  const { t } = useTranslation(["tasks"]);
  const [isCommentFieldVisible, setIsCommentFieldVisible] = useState(false);
  const form = Form.useFormInstance();

  const { data: editingRequests, isLoading } = useGetVerificationEditingRequests({ farmId });

  // Only one editing request per type will be displayed.
  const key = editingRequestIdentifierMap[type];

  const activeEditingRequest = editingRequests?.find((r) => {
    const matchesId = id ? r[key] === id : r[key] === null || r[key] === "";
    const matchesType = r.requestType === type;
    const isNotCompleted = r.completedAt === null;
    const matchesRequestId = requestId ? r.id === requestId : true;

    return matchesId && matchesType && isNotCompleted && matchesRequestId;
  });

  const toggleCommentField = useCallback(() => {
    if (!isCommentFieldVisible) {
      setIsCommentFieldVisible(true);
    } else {
      // Clear the form field value when hiding
      const currentValues = form.getFieldsValue();
      form.setFieldsValue({
        ...currentValues,
        editingRequestComment: {
          ...currentValues.editingRequestComment,
          [type]: undefined,
        },
      });
      setIsCommentFieldVisible(false);
    }
  }, [isCommentFieldVisible, form, type]);

  if (isLoading || !activeEditingRequest) return null;

  return (
    <AlertBanner
      className="margin-top-100 margin-bottom-100"
      dataTestId="edit-request-banner"
      description={
        <>
          <p className="margin-top-0 margin-bottom-0">{activeEditingRequest.comment}</p>
          {isCommentFieldVisible && (
            <div style={{ animation: "fade-down 0.1s ease-in" }}>
              <TextArea
                dataTestId="edit-request-banner-textarea"
                name={["editingRequestComment", type]}
                className="margin-bottom-50"
                label={t("editingRequestBanner.commentLabel")}
                autoSize
              />
            </div>
          )}
        </>
      }
      link
      linkText={t(`editingRequestBanner.${isCommentFieldVisible ? "cancelButton" : "commentButton"}`)}
      type="warning"
      iconName="tabler/alert-triangle"
      iconAlign="start"
      onClick={toggleCommentField}
    />
  );
};
